import {effect, EffectRef, Injector, Signal} from '@angular/core';

export interface SignalMatchesOptions {
    /**
     * Optional Injector to use for effect context.
     * Required if called outside an injection context.
     */
    injector?: Injector;

    /**
     * If true, the promise will always resolve asynchronously
     */
    asyncResolve?: boolean;
}

/**
 * Returns a promise that resolves when a signal satisfies a predicate.
 *
 * @param signal - The signal to watch
 * @param predicate - Function returning true when the promise should resolve
 * @param options - Optional flags: injector, asyncResolve
 */
export function whenSignalMatches<T>(
    signal: Signal<T>,
    predicate: (value: T) => boolean,
    options: SignalMatchesOptions = {}
): Promise<T> {
    const {injector, asyncResolve = false} = options;
    let effectRef: EffectRef;

    return new Promise<T>(resolve => {
        effectRef = effect(() => {
            const value: T = signal();

            if (predicate(value)) {
                effectRef.destroy(); // stop the effect to avoid memory leaks

                if (asyncResolve) {
                    setTimeout(() => resolve(value));
                } else {
                    resolve(value);
                }
            }
        }, {injector});
    });
}

/**
 * Creates an effect that tracks a single signal and provides the previous value to the callback.
 * Only calls the callback after the signal has had a non-undefined value at least once.
 *
 * @param signal - the signal to track
 * @param callback - called on changes with the previous value (only after signal has been non-undefined)
 * @param injector - Required if called outside an injection context
 * @returns EffectRef
 */
export function effectOnSignalSet<T>(
    signal: Signal<T>,
    callback: (previousValue: T) => any | (() => any),
    injector?: Injector
): EffectRef {
    let previousValue: T = undefined;

    return effect(() => {
        const currentValue: T = signal();

        // Skip while the signal has never been defined
        if (previousValue === undefined && currentValue === undefined) {
            return;
        }

        // Call callback with previous value (which may be undefined on first defined value)
        callback(previousValue);
        previousValue = currentValue;
    }, {injector});
}
